import { filter } from 'rxjs/operators';
import { Component, ElementRef, Renderer2, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { CommandHandlerResolver } from '../../../core/service/command/command-handler-resolver.service';
import { ComponentMessageDispatcher } from '../../../core/service/component-message-dispather.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { EmailStatus } from '../../model/const/enums';
import { Email } from '../../model/email';
import { EmailService } from '../../service/data/email.service';
import { standardCommand } from '../../../core/model/command';
import { Observable } from 'rxjs';
import { FileService } from '../../../core/service/file.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { LanguageService } from '../../../core/service/language.service';

@Component({
    selector: 'app-send-lease-agreement-email',
    templateUrl: './send-tenant-email-form.component.html',
    styleUrls: ['./send-tenant-email-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SendTenantEmailFormComponent extends FormComponentBase<Email> {
  constructor(
    private _messageDispather: ComponentMessageDispatcher,
    activatedRoute: ActivatedRoute,
    dispatcher: CommandDispatcher,
    private languageService: LanguageService,
    protected fb: FormBuilder,
    protected fc: FormControlExFactory,
    handlerResolver: CommandHandlerResolver,
    protected service: EmailService,
    messageService: MessageService,
    navigation: NavigationService,
    private _fileService: FileService,
    private _renderer: Renderer2,
    private _router: Router,
    private _route: ActivatedRoute
  ) {
    super(activatedRoute, service, messageService, navigation, dispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);

    this.AddSubscription(
      this._messageDispather.messages.pipe(filter(message => message.tenants)).subscribe(message => {
        _messageDispather.messageDelivered();
        this.recipients = message.tenants.slice().filter(
          (r, index, arr) => index === arr.findIndex(x => x.recipientName === r.recipientName)
        );
        this.showRecipients = this.recipients.length > 0;
      })
    );
    this.restoreRecipients();
  }

  // ngOnInit(): void {
  // }

  recipients: any[];
  showRecipients: boolean = false;
  showPreview = false;

  get hasRecipients(): boolean {
    return Array.isArray(this.recipients) && this.recipients.length > 0;
  }

  get canSend(): boolean {
    return this.form && this.form.valid;
  }

  get isSendDisabled(): boolean {
    return !this.form || this.form.invalid || this.form.disabled || !this.hasRecipients;
  }

  get showSend(): boolean {
    return this.dataContext && this.dataContext.status === EmailStatus.Created;
  }

  protected createModel(): Email {
    return new Email();
  }

  protected getFormGroup(): FormGroup {
    return this.fb.group({
      body: this.fc.createMandatory('MAIL_FORM.BODY_SMS_LABEL', this.dataContext.body),
      subject: this.fc.createMandatory('MAIL_FORM.HEADER_LABEL', this.dataContext.subject),
      attachments: this.fb.array([]),
    });
  }

  protected init(emailMessage: Email) {
    super.init(emailMessage);
  }

  private _getFormData() {
    let data = this.form.value;
    data.attachments = (this.form.controls['attachments'] as FormArray).controls.map(f => f.value.data);
    data.leaseAgreements = (this.recipients ?? []).map(recipient => recipient.id);
    return data;
  }

  private clearRecipients() {
    this.recipients = [];
    this.showRecipients = false;
  }

  private restoreRecipients() {
    let recipient = this._route.snapshot.queryParams['tmpusr'];
    if (recipient) {
      this.recipients = [{ recipientName: recipient }];
      this.showRecipients = true;
      this._router.navigate([], {
        relativeTo: this._route,
        queryParams: { tmpusr: null },
        queryParamsHandling: 'merge',
      });
      return;
    }

    if (this.hasRecipients) return;

    this.handleMissingRecipients();
  }

  private handleMissingRecipients() {
    this.clearRecipients();
    this.messageService.showError('COMMON.RECIPIENTS_REQUIRED');
    this.navigation.navigate(['email', 'recipients']);
  }

  removeRecipient(recipient, index: number) {
    let message = this.languageService.instant('COMMON.CONFIRM_REMOVE_FROM_LIST');
    if (!confirm(message.replace('{{x}}', recipient.recipientName))) return;
    this.recipients.splice(index, 1);
    if (this.recipients.length === 0) {
      this.handleMissingRecipients();
      return;
    }

  }

  cancel() {
    this.clearRecipients();
    this.navigation.navigateBack();
  }

  onSubmit(): void {
    if (this.form.invalid || this.form.pristine || this.form.disabled) return;
    this.form.disable();
    this.AddSubscription(
      this.service.createEmailLinkedToAgreements(this._getFormData()).subscribe(
        () => {
          this.clearRecipients();
          this.formSubmitted();
          this.messageService.showSuccess('SUCCESS.EMAIL_SAVED');
        },
        error => {
          this.messageService.showError(error);
          this.form.enable();
        }
      )
    );
  }

  canDeactivate(): boolean | Observable<boolean> | Promise<boolean> {
    //
    // Avoid save changes message
    //
    return true;
  }

  send() {
    if (this.form.invalid || this.form.pristine || this.form.disabled || !this.hasRecipients) return;
    this.form.disable();
    this.AddSubscription(
      this.service.sendEmailToTenants(this._getFormData()).subscribe(
        () => {
          this.clearRecipients();
          this.formSubmitted();
          this.messageService.showSuccess('SUCCESS.EMAIL_SENT');
          this.navigation.navigateBack();
        },
        error => {
          this.messageService.showError(error);
          this.form.enable();
        }
      )
    );
  }
}
